import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import type React from "react";
import type { KintoneSystem } from "~/functions/api/kintoneSystem";
import { toDisplayString } from "~/utils/customDate";
import { buildKintoneSpaceUrl } from "~/utils/kintoneUrl";
import styles from "./index.module.scss";

export interface AppListTableProps {
  systems: KintoneSystem[];
  onRowClick?: (systemId: string) => void;
}

const AppListTable: React.FC<AppListTableProps> = ({ systems, onRowClick }) => {
  const handleOpenKintone = (e: React.MouseEvent, url?: string) => {
    e.stopPropagation();
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleRowClick = (systemId: string) => {
    if (onRowClick) {
      onRowClick(systemId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, systemId: string) => {
    if ((e.key === "Enter" || e.key === " ") && onRowClick) {
      e.preventDefault();
      onRowClick(systemId);
    }
  };

  return (
    <div className={styles["appListTable"]}>
      <table>
        <thead>
          <tr>
            <th className={styles["colSpace"]}>スペース</th>
            <th className={styles["colSubdomain"]}>サブドメイン</th>
            <th className={styles["colStatus"]}>ステータス</th>
            <th className={styles["colCreator"]}>作成者</th>
            <th className={styles["colDate"]}>作成日</th>
            <th className={styles["colAction"]}></th>
          </tr>
        </thead>
        <tbody>
          {systems.map((system) => {
            const kintoneUrl = buildKintoneSpaceUrl(system);
            return (
              <tr
                key={system.id}
                onClick={() => handleRowClick(system.id)}
                onKeyDown={(e) => handleKeyDown(e, system.id)}
                className={onRowClick ? styles["clickableRow"] : ""}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? "button" : undefined}
              >
                <td className={styles["spaceCell"]}>
                  <span className={styles["spaceName"]}>{system.space.name}</span>
                </td>
                <td className={styles["subdomainCell"]}>
                  <span className={styles["subdomain"]}>{system.subdomain}</span>
                </td>
                <td className={styles["statusCell"]}>
                  <span className={styles[`status-${system.status}`]}>
                    {system.status === "succeeded"
                      ? "完了"
                      : system.status === "running"
                        ? "処理中"
                        : system.status === "queued"
                          ? "待機中"
                          : system.status === "failed"
                            ? "失敗"
                            : system.status === "canceled"
                              ? "キャンセル"
                              : system.status}
                  </span>
                </td>
                <td className={styles["creatorCell"]}>{system.created_by || "-"}</td>
                <td className={styles["dateCell"]}>{toDisplayString(system.created_at)}</td>
                <td className={styles["actionCell"]}>
                  <button
                    type="button"
                    className={styles["kintoneButton"]}
                    onClick={(e) => handleOpenKintone(e, kintoneUrl)}
                    disabled={system.status !== "succeeded" || !kintoneUrl}
                  >
                    kintone
                    <OpenInNewIcon sx={{ width: 14, height: 14 }} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default AppListTable;
